/*
 * @FileName       : createReducer.ts
 * @Version        : 1.0
 * @DateOfCreation : 04/21/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : Generic function to create reducer.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

// Type safe reducer creator
// Takes in 2 generic types, TState and TAction
// TState is the type of the state object
// TAction is the type of the action object ,
// Here we only allow actions with a type property of type string hence the extends clause
export default function createReducer<TState, TAction extends { type: string }>(
  // Takes in the initial state of the reducer
  initialState: TState,
  // Takes in an object of handlers
  // The key of the object is the type of the action
  // The value of the object is a function that takes in the state and the action and returns the new state
  handlers: {
    // P in TAction['type'] ensures that keys of the object is of same type as the type property of the action
    // As keys are used to define the action type, this ensures that the action type is a valid action type
    // Here P will be of type string as the type property of the action is of type string
    [P in TAction["type"]]?: TAction extends { type: P }
    // The value of the key must contain a function that takes in the state and the action and returns the new state
      ? (state: TState, action: TAction) => TState
      : never;
  }
) {
  return (state: TState = initialState, action: TAction): TState => {
    // We check if the handlers object has a property with the action type
    // Here action.type is of type string as we have extended the TAction type to have a type property of type string
    if (handlers.hasOwnProperty(action.type)) {
      // If the handlers object has a property with the action type, we call the function with the state and the action
      // The function returns the new state
      return (handlers as any)[action.type](state, action);
    } else {
      // If the handlers object does not have a property with the action type, we return the state
      return state;
    }
  };
}
